import { Badge, Button, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, propose } from '../api';
import { AskAgent } from '../components/AskAgent';
import { Field } from '../components/Field';
import { useApi } from '../hooks';
import { useSession } from '../session';
import { ErrorBanner, Loading, outcomeMessage, outcomeTone, useToast, badgeTone } from '../components/ui';

// OKRs (ADR 0037), pieces of the app the OKR coach brings: the map of every
// objective on the business model page (okr_map), and a person's own on Home
// (my_okrs), where they correct what their work serves. Values come from the
// server: measured from the records, beside the owner's check-in. Patterns:
// data-display/tree-view (the map), user-feedback/progress-indicator (a key
// result's bar), forms/selection-input (what a piece of work serves),
// ai-intelligence/ai-chat (working objectives out with the coach).

interface KeyResult {
  id: string; title: string; measure: string; start: number; target: number; currency: string | null; measured: number | null; value: number | null;
  check_in: { value: number | null; confidence: number | null; confidence_before: number | null; note: string | null; at: string; by: string | null } | null; differs: boolean; progress: number; status: Standing; linked: number; version: number;
}
type Standing = 'achieved' | 'on_pace' | 'behind';
interface Objective {
  id: string; level: 'business' | 'team' | 'person'; team: { id: string; name: string } | null; owner: { id: string; name: string }; parent_id: string | null;
  period: string; title: string; why: string | null; status: string; drafted: boolean; version: number; progress: number; standing: Standing; can_set: boolean; key_results: KeyResult[];
}
interface Okrs { period: string; today: string; elapsed: number; coach: { id: string; status: string } | null; objectives: Objective[]; work: { result: number; other: number; admin: number; unreviewed: number } }
interface Work { type: string; id: string; title: string; kind: string; status: string; owner: { id: string; name: string } | null; at: string; alignment: { serves: string; key_result_ids: string[]; by: 'person' | 'coach' } | null }
interface Team { id: string; name: string; status: string; manager_user_id: string | null; members: string[] }
interface Person { id: string; display_name: string; status: string }

const STANDING: Record<Standing, [string, string]> = { achieved: ['Achieved', 'ok'], on_pace: ['On pace', 'ok'], behind: ['Behind', 'danger'] };
const MEASURES: Array<[string, string]> = [['deals_won', 'Deals won'], ['won_value', 'Value of deals won'], ['new_leads', 'New leads'], ['work_done', 'Work done'], ['check_in', 'Checked in by its owner']];
const KIND_WORDS: Record<string, string> = { call: 'Call', meeting: 'Meeting', task: 'Task', deadline: 'Deadline', email: 'Email', deal: 'Deal', lead: 'Lead', campaign: 'Campaign' };

const fmt = (kr: KeyResult, v: number | null) => (v === null ? '—' : kr.currency
  ? new Intl.NumberFormat(undefined, { style: 'currency', currency: kr.currency, maximumFractionDigits: 0 }).format(v) : new Intl.NumberFormat().format(v));
const levelName = (o: Objective) => (o.level === 'business' ? 'Business' : o.level === 'team' ? o.team!.name : o.owner.name);

// The quarter before today's, today's, the one after, and the year.
function periods(today: string): string[] {
  const y = Number(today.slice(0, 4));
  const q = Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1;
  const at = (n: number) => `${y + Math.floor((n - 1) / 4)}-Q${((n - 1 + 400) % 4) + 1}`;
  return [at(q - 1), at(q), at(q + 1), String(y)];
}

function useSend(done: () => unknown) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const send = async (schema: string, payload: Record<string, unknown>, ok: string) => {
    setBusy(true);
    try {
      const o = await propose(schema, payload);
      toast(outcomeMessage(o, ok), outcomeTone(o));
      if (o.state === 'written') await done();
      return o.state === 'written';
    } catch (e) { toast((e as Error).message, 'error'); return false; } finally { setBusy(false); }
  };
  return { busy, send };
}

function StandingChip({ s }: { s: Standing }) {
  const [label, tone] = STANDING[s];
  return <Badge tone={badgeTone(tone)}>{label}</Badge>;
}

function KeyResultRow({ kr, canSet, onChanged }: { kr: KeyResult; canSet: boolean; onChanged: () => unknown }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [confidence, setConfidence] = useState('');
  const { busy, send } = useSend(async () => { setOpen(false); setValue(''); setNote(''); setConfidence(''); await onChanged(); });
  const counted = kr.measure !== 'check_in';
  const sure = kr.check_in?.confidence ?? null;
  const before = kr.check_in?.confidence_before ?? null;
  return (
    <li className="okr-kr">
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span>{kr.title}</span>
        <StandingChip s={kr.status} />
      </div>
      <div className="okr-bar" role="progressbar" aria-label={`${kr.title}: ${kr.progress}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={kr.progress}><span style={{ width: `${kr.progress}%` }} /></div>
      <div className="small muted">
        {counted ? `Measured ${fmt(kr, kr.measured)} of ${fmt(kr, kr.target)}, from ${kr.linked} linked piece${kr.linked === 1 ? '' : 's'} of work` : `Checked in ${fmt(kr, kr.value)} of ${fmt(kr, kr.target)}`}
        {counted && kr.check_in && <> · check-in {fmt(kr, kr.check_in.value)}{kr.check_in.note ? `: ${kr.check_in.note}` : ''}</>}
        {sure !== null && <> · {sure} of 10 sure{before !== null && before !== sure ? `, ${before > sure ? 'down' : 'up'} from ${before}` : ''}</>}
      </div>
      {kr.differs && <div className="small okr-differs">The check-in says {fmt(kr, kr.check_in!.value)}; the records show {fmt(kr, kr.measured)}. Log the missing work, or link it to this result.</div>}
      {canSet && !open && <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>Check in</Button>}
      {open && (
        <form className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}
          onSubmit={(e) => { e.preventDefault(); if (value.trim() !== '') void send('key_result.update', { key_result_id: kr.id, base_version: kr.version, check_in: { value: Number(value), ...(confidence !== '' ? { confidence: Number(confidence) } : {}), ...(note.trim() ? { note: note.trim() } : {}) } }, 'Checked in'); }}>
          <Field label="Where it stands"><input className={fieldClass} type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} style={{ width: 120 }} /></Field>
          <Field label="How sure you are of the target">
            <select className={selectClass} value={confidence} onChange={(e) => setConfidence(e.target.value)}>
              <option value="">Not saying</option>
              {Array.from({ length: 11 }, (_, n) => <option key={n} value={n}>{n === 0 ? '0, no chance' : n === 5 ? '5, even odds' : n === 10 ? '10, certain' : n}</option>)}
            </select>
          </Field>
          <Field label="What it means" className="grow"><input className={fieldClass} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <Button type="submit" variant="secondary" size="sm" disabled={busy || value.trim() === ''}>Save</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
        </form>
      )}
    </li>
  );
}

function ObjectiveCard({ o, onChanged, children }: { o: Objective; onChanged: () => unknown; children?: ReactNode }) {
  const { busy, send } = useSend(onChanged);
  const status = (s: string, ok: string) => send('objective.update', { objective_id: o.id, base_version: o.version, set: { status: s } }, ok);
  return (
    <li className="okr-objective" aria-label={o.title}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <strong>{o.title}</strong> <span className="small muted">{levelName(o)} · {o.period}</span>
        </div>
        {o.status === 'draft' ? <Badge tone="info">Draft from the OKR coach</Badge>
          : o.status === 'active' ? <span className="row" style={{ gap: 6 }}><span className="small">{o.progress}%</span><StandingChip s={o.standing} /></span>
            : <Badge>{o.status === 'achieved' ? 'Achieved' : o.status === 'missed' ? 'Missed' : 'Dropped'}</Badge>}
      </div>
      {o.why && <p className="small muted" style={{ margin: 0 }}>{o.why}</p>}
      <ul className="okr-krs" aria-label={`Key results of ${o.title}`}>
        {o.key_results.map((k) => <KeyResultRow key={k.id} kr={k} canSet={o.can_set && o.status === 'active'} onChanged={onChanged} />)}
      </ul>
      {o.status === 'draft' && o.can_set && (
        <div className="row" style={{ gap: 8 }}>
          <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void status('active', 'Adopted')}>Adopt</Button>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void status('dismissed', 'Dismissed')}>Dismiss</Button>
        </div>
      )}
      {children}
    </li>
  );
}

// Each objective under the one it serves; those that serve none sit at the top.
function Tree({ all, parent, onChanged }: { all: Objective[]; parent: string | null; onChanged: () => unknown }) {
  const known = new Set(all.map((o) => o.id));
  const here = all.filter((o) => (parent ? o.parent_id === parent : !o.parent_id || !known.has(o.parent_id)));
  if (!here.length) return null;
  return (
    <ul className="okr-tree">
      {here.map((o) => <ObjectiveCard key={o.id} o={o} onChanged={onChanged}><Tree all={all} parent={o.id} onChanged={onChanged} /></ObjectiveCard>)}
    </ul>
  );
}

function CoachState({ coach }: { coach: Okrs['coach'] }) {
  if (!coach || coach.status === 'active') return null;
  return <p className="small muted" style={{ margin: 0 }}>The OKR coach is not turned on yet. Once it is, it links new work each weekday morning and talks you through setting objectives. <Link to={`/agents/${encodeURIComponent(coach.id)}`}>Open the OKR coach</Link></p>;
}

// The OKR coach talks a person through setting objectives (ai-intelligence/ai-chat): it explains, suggests
// from their records, and asks; what they agree lands here as a draft to adopt. Opened on request.
function CoachChat({ level, period, onAnswered }: { level: 'business' | 'person'; period: string; onAnswered: () => unknown }) {
  const whose = level === 'business' ? 'the business\'s objectives' : 'your objectives';
  return (
    <AskAgent base="/v1/okrs" title={`Work out ${whose} with the OKR coach`} fallbackName="OKR coach" body={{ level, period }} onAnswered={onAnswered}
      starters={[level === 'business' ? 'Help me set the business\'s objectives' : 'Help me set my objectives', 'What are OKRs?']}
      hint="{name} explains as it goes, suggests from your records, and asks you to choose. Nothing is set until you adopt a draft." working="is thinking it through" />
  );
}

// A new objective with its key results; who may set each level is the server's to decide.
function NewObjective({ okrs, onDone }: { okrs: Okrs; onDone: () => unknown }) {
  const { me } = useSession();
  const teams = useApi<Team[]>('/v1/teams');
  const people = useApi<Person[]>('/v1/users');
  const [level, setLevel] = useState<'business' | 'team' | 'person'>(['owner', 'admin'].includes(me.role) ? 'business' : 'person');
  const [who, setWho] = useState('');
  const [parent, setParent] = useState('');
  const [title, setTitle] = useState('');
  const [why, setWhy] = useState('');
  const [krs, setKrs] = useState([{ title: '', measure: 'deals_won', target: '', currency: 'USD' }]);
  const { busy, send } = useSend(async () => { setTitle(''); setWhy(''); setKrs([{ title: '', measure: 'deals_won', target: '', currency: 'USD' }]); await onDone(); });
  const parents = okrs.objectives.filter((o) => o.status === 'active' && o.level !== 'person');
  const ready = !!title.trim() && krs.every((k) => !!k.title.trim() && k.target !== '') && (level === 'business' || level === 'person' || who);
  const submit = () => send('objective.create', {
    level, period: okrs.period, title: title.trim(), ...(why.trim() ? { why: why.trim() } : {}), ...(parent ? { parent_id: parent } : {}),
    ...(level === 'team' ? { team_id: who } : level === 'person' ? { user_id: who || me.user_id } : {}),
    key_results: krs.map((k) => ({ title: k.title.trim(), measure: k.measure, target: Number(k.target), ...(k.measure === 'won_value' ? { currency: k.currency } : {}) })),
  }, 'Objective set');
  const setKr = (i: number, patch: Partial<(typeof krs)[number]>) => setKrs(krs.map((k, j) => (j === i ? { ...k, ...patch } : k)));
  return (
    <form className="stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); if (ready) void submit(); }} aria-label="New objective">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="For">
          <select className={selectClass} value={level} onChange={(e) => { setLevel(e.target.value as typeof level); setWho(''); }}>
            <option value="business">The business</option><option value="team">A team</option><option value="person">A person</option>
          </select>
        </Field>
        {level === 'team' && <Field label="Team"><select className={selectClass} value={who} onChange={(e) => setWho(e.target.value)}><option value="">Choose a team</option>{teams.data?.filter((t) => t.status === 'active').map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>}
        {level === 'person' && <Field label="Person"><select className={selectClass} value={who || me.user_id} onChange={(e) => setWho(e.target.value)}>{(people.data ?? [{ id: me.user_id, display_name: me.display_name, status: 'active' }]).filter((p) => p.status === 'active').map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}</select></Field>}
        {level !== 'business' && parents.length > 0 && <Field label="Serves"><select className={selectClass} value={parent} onChange={(e) => setParent(e.target.value)}><option value="">No objective above it</option>{parents.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}</select></Field>}
      </div>
      <Field label="Objective"><input className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What to achieve this period" /></Field>
      <Field label="Why it matters"><input className={fieldClass} value={why} onChange={(e) => setWhy(e.target.value)} /></Field>
      {krs.map((k, i) => (
        <fieldset key={i} className="row okr-kr-input" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <legend className="small">Key result {i + 1}</legend>
          <Field label="Result" className="grow"><input className={fieldClass} value={k.title} onChange={(e) => setKr(i, { title: e.target.value })} placeholder="Win 12 new clients" /></Field>
          <Field label="Measured by"><select className={selectClass} value={k.measure} onChange={(e) => setKr(i, { measure: e.target.value })}>{MEASURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
          <Field label="Target"><input className={fieldClass} type="number" step="any" value={k.target} onChange={(e) => setKr(i, { target: e.target.value })} style={{ width: 110 }} /></Field>
          {k.measure === 'won_value' && <Field label="Currency"><input className={fieldClass} value={k.currency} maxLength={3} onChange={(e) => setKr(i, { currency: e.target.value.toUpperCase() })} style={{ width: 70 }} /></Field>}
        </fieldset>
      ))}
      <div className="row" style={{ gap: 8 }}>
        <Button type="button" variant="ghost" size="sm" onClick={() => setKrs([...krs, { title: '', measure: 'deals_won', target: '', currency: 'USD' }])}>Another key result</Button>
        <Button type="submit" variant="secondary" disabled={busy || !ready}>Set objective</Button>
      </div>
    </form>
  );
}

// okr_map, on the business model page: every objective of the period as a tree, and where the work went.
export function OkrMap() {
  const { me } = useSession();
  const [period, setPeriod] = useState<string | null>(null);
  const r = useApi<Okrs>(`/v1/okrs${period ? `?period=${period}` : ''}`, [period]);
  const [adding, setAdding] = useState(false);
  const [chatting, setChatting] = useState(false);
  const d = r.data;
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="okr-h">
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <h2 id="okr-h">Objectives and key results</h2>
          <div className="faint">The business's objectives, each team's, and each person's you may see, under the one each serves. Measured from the work linked to them; a check-in shows beside it.</div>
        </div>
        {d && <Field label="Period"><select className={selectClass} value={d.period} onChange={(e) => setPeriod(e.target.value)}>{[...new Set([...periods(d.today), d.period])].map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>}
      </div>
      <ErrorBanner error={r.error} />
      {!d ? <Loading label="Loading objectives" /> : <>
        <CoachState coach={d.coach} />
        <dl className="okr-work" aria-label="Where this period's work went">
          <div><dt>Serves a result</dt><dd>{d.work.result}</dd></div>
          <div><dt>Not tied to a result</dt><dd>{d.work.other}</dd></div>
          <div><dt>Admin</dt><dd>{d.work.admin}</dd></div>
          <div><dt>Not reviewed yet</dt><dd>{d.work.unreviewed}</dd></div>
        </dl>
        {d.objectives.length ? <Tree all={d.objectives} parent={null} onChanged={() => r.reload()} /> : <p className="muted" style={{ margin: 0 }}>No objectives for {d.period} yet.</p>}
        <div className="row" style={{ gap: 8 }}>
          {!adding && <Button type="button" variant="secondary" size="sm" onClick={() => setAdding(true)}>New objective</Button>}
          {!chatting && d.coach?.status === 'active' && ['owner', 'admin'].includes(me.role) && <Button type="button" variant="ghost" size="sm" onClick={() => setChatting(true)}>Work out the business's objectives with the coach</Button>}
        </div>
        {adding && <NewObjective okrs={d} onDone={async () => { setAdding(false); await r.reload(); }} />}
        {chatting && <CoachChat level="business" period={d.period} onAnswered={() => r.reload()} />}
      </>}
    </section>
  );
}

// What a piece of work serves, as one choice: a key result, not tied to a result, or admin.
function ServesSelect({ w, options, onChanged }: { w: Work; options: Objective[]; onChanged: () => unknown }) {
  const { busy, send } = useSend(onChanged);
  const value = !w.alignment ? '' : w.alignment.serves === 'result' ? w.alignment.key_result_ids[0]! : w.alignment.serves;
  const change = (v: string) => send('work.align', { items: [{ type: w.type, id: w.id, ...(v === 'other' || v === 'admin' ? { serves: v } : { serves: 'result', key_result_ids: [v] }) }] }, 'Saved');
  return (
    <select className={selectClass} aria-label={`What ${w.title} serves`} value={value} disabled={busy} onChange={(e) => void change(e.target.value)}>
      {!w.alignment && <option value="" disabled>Not reviewed yet</option>}
      {options.map((o) => <optgroup key={o.id} label={o.title}>{o.key_results.map((k) => <option key={k.id} value={k.id}>{k.title}</option>)}</optgroup>)}
      <option value="other">Not tied to a result</option>
      <option value="admin">Admin</option>
    </select>
  );
}

// my_okrs, on Home: the person's own objectives and their teams', and their recent work with what it serves.
export function MyOkrs() {
  const { me } = useSession();
  const r = useApi<Okrs>('/v1/okrs');
  const teams = useApi<Team[]>('/v1/teams');
  const work = useApi<{ items: Work[]; next: string | null }>('/v1/okrs/work');
  // Pages after the first, fetched on Show more; cleared when the first page reloads.
  const [more, setMore] = useState<{ items: Work[]; next: string | null } | null>(null);
  const [chatting, setChatting] = useState(false);
  const toast = useToast();
  if (r.error) return <ErrorBanner error={r.error} />;
  if (!r.data) return null;
  const mine = new Set((teams.data ?? []).filter((t) => t.members.includes(me.user_id) || t.manager_user_id === me.user_id).map((t) => t.id));
  const shown = r.data.objectives.filter((o) => o.status !== 'draft' || o.can_set).filter((o) => (o.level === 'person' && o.owner.id === me.user_id) || (o.level === 'team' && mine.has(o.team!.id)));
  const active = r.data.objectives.filter((o) => o.status === 'active' && o.key_results.length);
  const reload = () => { setMore(null); return Promise.all([r.reload(), work.reload()]); };
  const items = [...(work.data?.items ?? []), ...(more?.items ?? [])];
  const next = more ? more.next : work.data?.next ?? null;
  const showMore = async () => {
    try {
      const page = await api<{ items: Work[]; next: string | null }>(`/v1/okrs/work?after=${encodeURIComponent(next!)}`);
      setMore({ items: [...(more?.items ?? []), ...page.items], next: page.next });
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="my-okr-h">
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <h2 id="my-okr-h">Your OKRs</h2>
        <span className="small muted">{r.data.period}, {r.data.elapsed}% gone</span>
      </div>
      <CoachState coach={r.data.coach} />
      {shown.length ? <ul className="okr-tree">{shown.map((o) => <ObjectiveCard key={o.id} o={o} onChanged={reload} />)}</ul>
        : <p className="muted small" style={{ margin: 0 }}>No objectives of yours or your team's for {r.data.period} yet.</p>}
      <div className="row" style={{ gap: 8 }}>
        <Link to="/admin/business-model" className="small">All objectives</Link>
        {!chatting && r.data.coach?.status === 'active' && <Button type="button" variant="ghost" size="sm" onClick={() => setChatting(true)}>Work out yours with the coach</Button>}
      </div>
      {chatting && <CoachChat level="person" period={r.data.period} onAnswered={() => r.reload()} />}
      {!!items.length && (
        <div className="stack" style={{ gap: 6 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Your work this period and what it serves</h3>
          <p className="small muted" style={{ margin: 0 }}>The OKR coach links your work each morning. Change any it got wrong; it never changes what you set.</p>
          <ul className="okr-work-list">
            {items.map((w) => (
              <li key={`${w.type}:${w.id}`}>
                <span><span className="small muted">{KIND_WORDS[w.kind] ?? w.kind}</span> {w.title}</span>
                <ServesSelect w={w} options={active} onChanged={reload} />
              </li>
            ))}
          </ul>
          {next && <Button type="button" variant="ghost" size="sm" onClick={() => void showMore()}>Show more</Button>}
        </div>
      )}
    </section>
  );
}
